import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Alert, Heading, Stack, Box, SegmentGroup, HStack, Text, Textarea, Field,Flex, Spacer, Input, Select, createListCollection, Portal, Button, Wrap, DataList, Container, Code} from '@chakra-ui/react';
import axios from 'axios';
import { LuArrowLeft as LuBack, LuDna, LuTestTube, LuTestTubes, LuFileSpreadsheet, LuFolderCheck as LuIngest, LuSquareCheck, LuUser, LuSearch, LuTextSearch as LuSearchResults} from "react-icons/lu"
import PageContent from 'components/ui/PageContent';
import PageTitle, {titleIconSize, titleMB} from 'components/ui/PageTitle';

function CDSPage() {
  const { cdsId } = useParams(); // from URL
  const navigate = useNavigate();
  const [cds, setCds] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCds = async() => {
      try {
        setLoading(true);
        const response = await axios.post(`/api/cds/${cdsId}`);
        setCds(response.data);
        console.log("CDS data:", response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching CDS', err);
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (cdsId) {
      fetchCds();
    }
  }, [cdsId]);

  const stats = [
    { label: "Gene ID", value: cds.gene_id },
    { label: "Name", value: cds.name },
    { label: "Product", value: cds.product },
    { label: "Note", value: cds.note },
    { 
      label: "Contig", 
      value: cds.contig_name,
      link: cds.record_id ? `/contig/${cds.record_id}` : null
    },
    { label: "Project", value: cds.project_name },
    { label: "Organism", value: cds.organism_name },
    { 
      label: "Sample", 
      value: cds.sample_name,
      link: cds.sample_id ? `/samples/${cds.sample_id}` : null
    },
    { label: "Start position", value: cds.location_start + 1 },
    { label: "End position", value: cds.location_end },
  ];

  return (
    <PageContent>
      {error && (
        <Alert status="error" mb={4}>
          {error}
        </Alert>
      )}
      
      <HStack align={"center"} gap="2em">
        <Box mb={titleMB} cursor="pointer" onClick={() => navigate(-1)}>
          <LuBack size={titleIconSize} mb={titleMB} strokeWidth="2.5" />
        </Box>
        <PageTitle icon={LuDna} title="CDS Details" />
      </HStack>

      {loading ? (
        <Text>Loading CDS information...</Text>
      ) : (
        <>
          <DataList.Root orientation={"horizontal"} >
              {stats.map((item) => (
                <DataList.Item key={item.label}>
                  <DataList.ItemLabel>{item.label}</DataList.ItemLabel>
                  <DataList.ItemValue>
                    <Container px="0">
                      {item.link ? (
                        <Link to={item.link} style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {item.value}
                        </Link>
                      ) : (
                        item.value
                      )}
                    </Container>
                  </DataList.ItemValue>
                </DataList.Item>
              ))}
          </DataList.Root>

          {/* Protein Sequence */}
          {cds.prot_sequence && (
            <Box mt={4}>
              <Heading size="md" mb={2}>Protein Sequence</Heading>
              <Code 
                display="block" 
                whiteSpace="pre-wrap" 
                p={4} 
                borderRadius="md"
                fontSize="sm"
                fontFamily="monospace"
                variant="outline"
              >
                {cds.prot_sequence}
              </Code>
            </Box>
          )}

          {/* Nucleotide Sequence */}
          {cds.nucl_sequence && (
            <Box mt={4}>
              <Heading size="md" mb={2}>Nucleotide Sequence</Heading>
              <Code 
                display="block" 
                whiteSpace="pre-wrap" 
                p={4} 
                borderRadius="md"
                fontSize="sm"
                fontFamily="monospace"
                variant="outline"
              >
                {cds.nucl_sequence}
              </Code>
            </Box>
          )}
        </>
      )}

    </PageContent>
  );
}

export default CDSPage;